import React from 'react';
import {
    DocumentLinesIcon,
    UserDocumentIcon
} from '../../../../../../../components/buttons/IconButtons';
import {
    CALCULATION_TYPE_MODEL,
    CALCULATION_TYPE_MODEL_NAME
} from "../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model";

/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTitle: Full Stack Developer
 */

export const DEDUCTION_POLICY_TAB = {
    GLOBAL: {
        key: 'global',
        name: 'GLOBAL',
        translation: 'global_deduction',
        icon: <DocumentLinesIcon width={24} height={24} />,
    },
    USER: {
        key: 'user',
        name: 'USER',
        translation: 'user_deductions',
        icon: <UserDocumentIcon width={24} height={24} />,
    },
    getTabs: () => Object.values(DEDUCTION_POLICY_TAB).filter(e => e?.key !== undefined),
};

export const STEPS_ADD_USER_DEDUCTION_POLICY = {
    SELECT_USER: {
        INDEX: 0,
        TRANS: 'select_user',
    },
    DEDUCTION_POLICY: {
        INDEX: 1,
        KEY: 'DEDUCTION_POLICY',
        TRANS: 'user_deductions',
    },
    EXCLUDED_GLOBAL_DEDUCTIONS: {
        INDEX: 2,
        KEY: 'EXCLUDED_GLOBAL_DEDUCTIONS',
        TRANS: 'excluded_global_deductions',
    },
    SUMMARY: {
        INDEX: 3,
        TRANS: 'summary',
    },
};

export const USER_DEDUCTION_ENTRY_MODEL = {
    name: '',
    calculationType: null,
    value: '',
    startDate: null,
    endDate: null,
    isValid: function () {
        // endDate is optional; only validated for range when present.
        return this.name !== '' && this.calculationType !== null && this.value !== ''
            && this.startDate !== null;
    },
    isEmpty: function () {
        // endDate is optional; ignored for the empty check.
        return this.name === '' && this.calculationType === null && this.value === ''
            && this.startDate === null;
    },
    isValidDateRange: function () {
        // endDate may be null, or must be greater than or equal to startDate.
        if (!this.startDate || !this.endDate) return true;
        return new Date(this.endDate) >= new Date(this.startDate);
    },
    isDuplicateName: function (deductions = []) {
        // Collection-level check: true when another entry shares this name (case-insensitive).
        const name = this.name.trim().toLowerCase();
        return deductions.filter(d => d.name.trim().toLowerCase() === name).length > 1;
    },
    set: function (data) {
        return {
            ...this,
            id: data.id,
            name: data.name ?? '',
            calculationType: data.calculationType ?? null,
            value: data.value != null ? String(data.value) : '',
            startDate: data.startDate ?? null,
            endDate: data.endDate ?? null,
        };
    },
};

export const DEDUCTION_CALCULATION_TYPE_MODEL = () =>
    CALCULATION_TYPE_MODEL().filter(c => c.id !== CALCULATION_TYPE_MODEL_NAME.MULTIPLIER);

export const USER_DEDUCTION_POLICY_MODEL = {
    toRegister: function (user, deductions, excludedGlobalRules) {
        return {
            userId: user[0]?.userId ?? user[0]?.id,
            deductions: deductions.filter(e => e.isValid()).map(({ name, calculationType, value, startDate, endDate }) => ({
                name,
                calculationType,
                value: parseFloat(value),
                startDate: new Date(startDate).getTime(),
                endDate: endDate ? new Date(endDate).getTime() : null,
            })),
            excludedDeductions: excludedGlobalRules.map(r => ({ payrollDeductionPolicyId: r.id })),
        };
    },
};

export const DEDUCTION_POLICY_MODEL = {
    id: null,
    name: '',
    calculationType: null,
    value: '',
    isMandatory: false,
    /**
     * Validates that all required fields are filled.
     * @returns {boolean}
     */
    isValid: function () {
        return this.name !== '' && this.calculationType !== null && this.value !== '';
    },
    /**
     * Serializes to API request body.
     * @returns {Object}
     */
    toRegister: function () {
        return {
            id: this.id,
            name: this.name,
            calculationType: this.calculationType,
            value: parseFloat(this.value),
            isMandatory: this.isMandatory,
        };
    },
    /**
     * Populates model from API response.
     * @param {Object} data
     * @returns {Object}
     */
    set: function (data) {
        return {
            ...this,
            id: data.id,
            name: data.name ?? '',
            calculationType: data.calculationType ?? null,
            value: data.value != null ? String(data.value) : '',
            isMandatory: data.isMandatory ?? false,
        };
    },
};
